import "./App.scss"

import { lazy as reactLazy, Suspense, useEffect, useMemo, useState } from "react"
import { createBrowserRouter, RouterProvider, Routes, Route } from "react-router-dom"
import { QueryParamProvider } from "use-query-params"
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6"
import { useTranslation } from "react-i18next"
import cx from "classnames"
import dayjs from "utils/dayjs"

import iconNavbarInventory from "assets/img/icon-navbar-inventory.svg"
import iconNavbarScheduler from "assets/img/icon-navbar-scheduler.svg"
import iconNavbarRequests from "assets/img/icon-navbar-request.svg"
import iconNavbarOutgoingInvoices from "assets/img/icon-navbar-outgoing-invoices.svg"
import iconNavbarShippingOrders from "assets/img/icon-navbar-shipping-orders.svg"
import iconNavbarShippingOrdersSledge from "assets/img/icon-navbar-shipping-orders-sledge.svg"
import iconNavbarFinance from "assets/img/icon-navbar-finance.svg"
import iconNavbarChecklist from "assets/img/icon-navbar-checklist.svg"
import iconNavbarPlus from "assets/img/icon-navbar-plus.svg"
import iconNavbarImport from "assets/img/icon-navbar-import-assets.svg"
import iconNavbarInvoices from "assets/img/icon-navbar-invoices.svg"
import iconNavbarValuation from "assets/img/icon-navbar-valuation.svg"
import iconNavbarMonthlyClosing from "assets/img/icon-navbar-monthly-closing.svg"
import iconNavbarGear from "assets/img/icon-gear.svg"
import iconNavbarLoupe from "assets/img/icon-navbar-loupe.svg"
import iconNavbarNotifications from "assets/img/icon-navbar-notifications.svg"
import iconNavbarHelp from "assets/img/icon-navbar-help.svg"
import iconBoxAndMagnifier from "assets/img/icon-box-and-magnifier.svg"
import iconQrCode from 'assets/img/icon-qrcode.svg'
import iconMaintenance from 'assets/img/icon-maintenance.svg'
import iconCustomers from 'assets/img/icon-customers.svg'
import iconNavbarFairValueEvaluation from 'assets/img/icon-fair-value-evaluation.svg'

import "utils/appInsights"
import i18n from "intl/i18n"
import Splash from "components/Splash/Splash"
import Navbar from "components/Navbar/Navbar"
import NavbarAppIcon from "components/Navbar/NavbarAppIcon"
import ToastMessages from "containers/ToastMessages/ToastMessages"
import PictureModals from "containers/PictureModals/PictureModals"
import AlertModals from "containers/AlertModals/AlertModals"
import ConfirmModals from "containers/ConfirmModals/ConfirmModals"
import ArticleSidebars from "containers/ArticleSidebars/ArticleSidebars"
import AssetSidebars from "containers/AssetSidebars/AssetSidebars"
import ReservationSidebars from "containers/ReservationSidebars/ReservationSidebars"
import { NavbarItemProps } from "components/Navbar/NavbarItem"
import ReferenceNumberSelectModal from "components/ReferenceNumberSelectModal/ReferenceNumberSelectModal"
import OfferCreateModal from "components/OfferCreateModal/OfferCreateModal"
import AppRoute from "models/AppRoute"
import { NavbarState, UserPermission } from "config/constants"
import AppRoutes from "@/routes"
import { useNavbarStore } from "stores/navbar"
import { initCommonStore, useCommonStore } from "stores/common"
import { useNotificationsStore } from "stores/notifications"
import useEnvIndicators from "hooks/useEnvIndicators"
import { registerNavigate } from "utils/router"

import { ReservationWorkflow } from "pages/ReservationPage/config"
import { OfferWorkflow } from "pages/OfferPage/config"
import { AssetWorkflow } from "pages/AssetPage/config"
import { ArticleWorkflow } from "pages/ArticlePage/config"
import InvoiceSidebars from "containers/InvoiceSidebars/InvoiceSidebars"
import { usePermissions } from "stores/permissions"
import UnreadNotificationsRefresh from "components/UnreadNotificationsRefresh/UnreadNotificationsRefresh"
import { useTenantSettings } from "stores/tenantSettings"
import GlobalAssetOverviewAssetSidebars from "containers/GlobalAssetOverviewAssetSidebars/GlobalAssetOverviewAssetSidebars"
import FairValueEvaluationPage from "pages/FairValueEvaluationPage/FairValueEvaluationPage"
import GlobalReservationSidebarHandler from "components/GlobalReservationSidebarHandler"
import GlobalAssetSidebarHandler from "components/GlobalAssetSidebarHandler"

// Catch that error and hard-reload so the user gets the new
// bundle instead of seeing a blank page.
// A sessionStorage flag prevents an infinite reload loop if the chunk keeps
// failing after the reload (e.g. real network error or broken deployment).
const CHUNK_RELOAD_KEY = "chunk_load_error_reloaded"

function lazy<T extends React.ComponentType<any>>(
    factory: () => Promise<{ default: T }>
) {
    return reactLazy(() =>
        factory().catch((error: unknown) => {
            const msg = error instanceof Error ? error.message : String(error)
            const isChunkLoadError =
                msg.includes("Failed to fetch dynamically imported module") ||
                msg.includes("error loading dynamically imported module") ||
                msg.includes("Importing a module script failed") ||
                (error instanceof Error && error.name === "ChunkLoadError")

            if (isChunkLoadError && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
                sessionStorage.setItem(CHUNK_RELOAD_KEY, "1")
                window.location.reload()
                console.log("New version reloaded")
                // Return a promise that never resolves – the reload above will
                // navigate away before React needs the component.
                return new Promise<{ default: T }>(() => undefined)
            }

            // Either not a chunk error, or we already reloaded once and it
            // still failed — clear the flag and let the error boundary handle it.
            sessionStorage.removeItem(CHUNK_RELOAD_KEY)
            throw error
        })
    )
}

const InventoryOverviewPage = lazy(() => import("pages/InventoryOverviewPage/InventoryOverviewPage"))
const ReservationPage = lazy(() => import("pages/ReservationPage/ReservationPage"))
const NotFoundPage = lazy(() => import("pages/NotFoundPage/NotFoundPage"))
const DashboardPage = lazy(() => import("pages/Dashboard/DashboardPage"))
const OfferPage = lazy(() => import("pages/OfferPage/OfferPage"))
const AssetPage = lazy(() => import("pages/AssetPage/AssetPage"))
const AssetImportPage = lazy(() => import("pages/AssetImportPage/AssetImportPage"))
const ArticlePage = lazy(() => import("pages/ArticlePage/ArticlePage"))
const SchedulerPage = lazy(() => import("pages/SchedulerPage/SchedulerPage"))
const OutgoingInvoicesPage = lazy(() => import("pages/OutgoingInvoicesPage/OutgoingInvoicesPage"))
const InvoicesPage = lazy(() => import("pages/InvoicesPage/InvoicesPage"))
const InvoiceAssignAmountPage = lazy(() => import("pages/InvoiceAssignAmountPage/InvoiceAssignAmountPage"))
const ValuationsPage = lazy(() => import("pages/ValuationsPage/ValuationsPage"))
const ValuationPage = lazy(() => import("pages/ValuationPage/ValuationPage"))
const TenantSettingsPage = lazy(() => import("pages/TenantSettingsPage/TenantSettingsPage"))
const AppSettingsPage = lazy(() => import("pages/AppSettingsPage/AppSettingsPage"))
const ProductCategoriesPage = lazy(() => import("pages/ProductCategoriesPage/ProductCategoriesPage"))
const ArticlesPage = lazy(() => import("pages/ArticlesPage/ArticlesPage"))
const UserSettingsPage = lazy(() => import("pages/UserSettingsPage/UserSettingsPage"))
const HelpPage = lazy(() => import("pages/HelpPage/HelpPage"))
const NoCompanyPage = lazy(() => import("pages/NoCompanyPage/NoCompanyPage"))
const NoAccessPage = lazy(() => import("pages/NoAccessPage/NoAccessPage"))
const OutgoingInvoiceCreatePage = lazy(() => import("pages/OutgoingInvoiceCreatePage/OutgoingInvoiceCreatePage"))
const AdHocOutgoingInvoiceCreatePage = lazy(() => import("pages/OutgoingInvoiceCreatePage/AdHocOutgoingInvoiceCreatePage"))
const OutgoingInvoicePage = lazy(() => import("pages/OutgoingInvoiceCreatePage/OutgoingInvoicePage"))
const OutgoingInvoiceDraftByDatePage = lazy(() => import("pages/OutgoingInvoiceCreatePage/OutgoingInvoiceDraftByDatePage"))
const RequestsPage = lazy(() => import("pages/RequestsPage/RequestsPage"))
const OffersPage = lazy(() => import("pages/OffersPage/OffersPage"))
const ManageReferenceValuesPage = lazy(() => import("pages/ManageReferenceValuesPage/ManageReferenceValuesPage"))
const CalculationDetailsPage = lazy(() => import("pages/CalculationDetailsPage/CalculationDetailsPage"))
const LinkLabelsPage = lazy(() => import("pages/LinkLabelsPage/LinkLabelsPage"))
const LinkLabelToAssetPage = lazy(() => import("pages/LinkLabelToAssetPage/LinkLabelToAssetPage"))
const PrepareShippingPage = lazy(() => import("pages/PrepareShippingPage/PrepareShippingPage"))
const NotificationsPage = lazy(() => import("pages/NotificationsPage/NotificationsPage"))
const ShippingOrdersPage = lazy(() => import("pages/ShippingOrdersPage/ShippingOrdersPage"))
const MonthlyClosingPage = lazy(() => import("pages/MonthlyClosingPage/MonthlyClosingPage"))
const StocktakingsPage = lazy(() => import("pages/StocktakingsPage/StocktakingsPage"))
const StocktakingNotScannedAssetsPage = lazy(() => import("pages/StocktakingNotScannedAssetsPage/StocktakingNotScannedAssetsPage"))
const RentToBuyChecklistPage = lazy(() => import("pages/RentToBuyChecklistPage/RentToBuyChecklistPage"))
const InvoiceImportPage = lazy(() => import("pages/InvoiceImportPage/InvoiceImportPage"))
const RolesAndPermissionsPage = lazy(() => import("pages/RolesAndPermissionsPage/RolesAndPermissionsPage"))
const GlobalAssetOverviewPage = lazy(() => import("pages/GlobalAssetOverviewPage/GlobalAssetOverviewPage"))
const MaintenancePage = lazy(() => import("pages/MaintenancePage/MaintenancePage"))
const CustomerOverviewPage = lazy(() => import("pages/CustomerOverviewPage/CustomerOverviewPage"))
const CustomerPage = lazy(() => import("pages/CustomerPage/CustomerPage"))
const CancelInvoicesPage = lazy(() => import("pages/CancelInvoicesPage/CancelInvoicesPage"))
const EarlyReturnPage = lazy(() => import("pages/EarlyReturnPage/EarlyReturnPage"))

const AppShell = () => {
    const { t } = useTranslation()
    const isI18nInitialized = i18n.isInitialized
    const { hasPermission } = usePermissions()
    const { globalAssetOverview } = useTenantSettings()

    useEnvIndicators()

    useEffect(() => {
        if (!isI18nInitialized) return
        initCommonStore()
    }, [isI18nInitialized])

    const [showReferenceNumberSelectModal, setShowReferenceNumberSelectModal] = useState(false)
    const [showCreateOfferModal, setShowCreateOfferModal] = useState(false)

    const commonStore = useCommonStore()
    const navbarStore = useNavbarStore()
    const { unreadNotificationCount } = useNotificationsStore()

    const isFinanceBlockHidden = useMemo(() =>
        !(hasPermission(UserPermission.IncomingInvoicesView) ||
            hasPermission(UserPermission.InvoicesImport) ||
            hasPermission(UserPermission.EvaluationAsset) ||
            hasPermission(UserPermission.MonthlyClosing) ||
            hasPermission(UserPermission.RentToBuyEvents)),
        [hasPermission])

    const navbarItems: NavbarItemProps[] = useMemo(() => {
        const items: NavbarItemProps[] = [
            {
                label: t("app.name"),
                route: AppRoutes.dashboard.getPath(),
                iconView: <NavbarAppIcon />,
            },
            {
                label: t('navbar.inventory.inventory'),
                route: AppRoutes.inventory.getPath(),
                iconImg: iconNavbarInventory,
                items: [
                    {
                        label: t('navbar.inventory.inventory'),
                        route: AppRoutes.inventory.getPath(),
                        showOnStateOnly: NavbarState.collapsed,
                    },
                    {
                        label: t('navbar.inventory.addAsset'),
                        route: AppRoutes.assetCreate.getPath(),
                        iconImg: iconNavbarPlus,
                        hidden: !hasPermission(UserPermission.AssetCreate),
                    },
                    {
                        label: t('navbar.inventory.importAssets'),
                        route: AppRoutes.assetImport.getPath(),
                        iconImg: iconNavbarImport,
                        hidden: !hasPermission(UserPermission.AssetCreate),
                    },
                    {
                        label: t('navbar.inventory.stocktakings'),
                        route: AppRoutes.stocktakings.getPath(),
                        iconImg: iconBoxAndMagnifier,
                    },
                    {
                        label: t('navbar.inventory.maintenance'),
                        route: AppRoutes.maintenance.getPath(),
                        iconImg: iconMaintenance,
                        hidden: !hasPermission(UserPermission.AssetMaintenanceView),
                    },
                    {
                        label: t('navbar.inventory.linkLabels'),
                        route: AppRoutes.linkLabels.getPath(),
                        iconImg: iconQrCode,
                        hidden: !hasPermission(UserPermission.LabelsView),
                    },
                    {
                        label: t('navbar.inventory.manageReferenceValues'),
                        action: () => setShowReferenceNumberSelectModal(true),
                        iconImg: iconNavbarGear,
                        hidden: !hasPermission(UserPermission.AssetEdit),
                    },
                    {
                        label: t('navbar.inventory.globalAssetOverview'),
                        route: AppRoutes.globalAssetOverview.getPath(),
                        iconImg: iconNavbarLoupe,
                        hidden: !hasPermission(UserPermission.GlobalAssetOverview) || !globalAssetOverview?.participation,
                    },
                ]
            },
            {
                label: t('navbar.requests.requests'),
                route: AppRoutes.requests.getPath(),
                iconImg: iconNavbarScheduler,
                items: [
                    {
                        label: t('navbar.requests.requests'),
                        route: AppRoutes.requests.getPath(),
                        showOnStateOnly: NavbarState.collapsed,
                    },
                    {
                        label: t('navbar.requests.scheduler'),
                        route: AppRoutes.scheduler.getPath(),
                        iconImg: iconNavbarScheduler
                    },
                    {
                        label: t('navbar.requests.requestsAndOffers'),
                        route: AppRoutes.requests.getPath(),
                        iconImg: iconNavbarRequests
                    },
                    {
                        label: t('navbar.requests.outgoingInvoices'),
                        route: AppRoutes.outgoingInvoices.getPath({ type: hasPermission(UserPermission.OutgoingInvoicesView) ? "created" : "to-be-created" }),
                        iconImg: iconNavbarOutgoingInvoices,
                        hidden: !hasPermission(UserPermission.OutgoingInvoicesView) && !hasPermission(UserPermission.OutgoingInvoicesCreate),
                    },
                    {
                        label: t('navbar.requests.createNewOffer'),
                        action: () => setShowCreateOfferModal(true),
                        iconImg: iconNavbarPlus,
                        className: "border-top-grey-100",
                        hidden: !hasPermission(UserPermission.OfferEdit),
                    }
                ]
            },
            {
                label: t('navbar.shippingOrders'),
                route: AppRoutes.shippingOrders.getPath(),
                iconImg: dayjs().month() === 11 // 11 is december as numeration starts from 0
                    ? iconNavbarShippingOrdersSledge
                    : iconNavbarShippingOrders,
                items: [
                    {
                        label: t('navbar.shippingOrders'),
                        route: AppRoutes.shippingOrders.getPath(),
                        showOnStateOnly: NavbarState.collapsed,
                    },
                ]
            },
            ...isFinanceBlockHidden ? [] : [{
                label: t('navbar.finance.finance'),
                route: hasPermission(UserPermission.IncomingInvoicesView) ? AppRoutes.invoices.getPath() : undefined,
                iconImg: iconNavbarFinance,
                items: [
                    {
                        label: t('navbar.finance.finance'),
                        route: hasPermission(UserPermission.IncomingInvoicesView) ? AppRoutes.invoices.getPath() : undefined,
                        showOnStateOnly: NavbarState.collapsed,
                    },
                    {
                        label: t('navbar.finance.incomingInvoices'),
                        route: AppRoutes.invoices.getPath(),
                        iconImg: iconNavbarInvoices,
                        hidden: !hasPermission(UserPermission.IncomingInvoicesView)
                    },
                    {
                        label: t('navbar.finance.importInvoices'),
                        route: AppRoutes.invoiceImport.getPath(),
                        iconImg: iconNavbarImport,
                        hidden: !hasPermission(UserPermission.InvoicesImport)
                    },
                    {
                        label: t('navbar.finance.evaluationOfAssets'),
                        route: AppRoutes.valuations.getPath(),
                        iconImg: iconNavbarValuation,
                        hidden: !hasPermission(UserPermission.EvaluationAsset)
                    },
                    {
                        label: t('navbar.finance.monthlyClosing'),
                        route: AppRoutes.monthlyClosing.getPath(),
                        iconImg: iconNavbarMonthlyClosing,
                        hidden: !hasPermission(UserPermission.MonthlyClosing)
                    },
                    {
                        label: t('navbar.finance.checklistRentToBuy'),
                        route: AppRoutes.rentToBuyChecklist.getPath(),
                        iconImg: iconNavbarChecklist,
                        hidden: !hasPermission(UserPermission.RentToBuyEvents)
                    },
                    {
                        label: t('navbar.finance.fairValueEvaluation'),
                        route: AppRoutes.fairValueEvaluation.getPath(),
                        iconImg: iconNavbarFairValueEvaluation,
                        hidden: !hasPermission(UserPermission.FairValueEvaluationView)
                    },
                ]
            }],
            {
                label: t('customer.customerOverview'),
                route: AppRoutes.customerOverview.getPath(),
                iconImg: iconCustomers,
                items: [
                    {
                        label: t('customer.customerOverview'),
                        route: AppRoutes.customerOverview.getPath(),
                        showOnStateOnly: NavbarState.collapsed,
                    },
                ]
            },
            {
                label: t('navbar.settings.settings'),
                route: AppRoutes.tenantSettings.getPath(),
                iconImg: iconNavbarGear,
                items: [
                    {
                        label: t('navbar.settings.settings'),
                        route: AppRoutes.tenantSettings.getPath(),
                        showOnStateOnly: NavbarState.collapsed,
                    },
                    {
                        label: t('navbar.settings.tenantSettings'),
                        route: AppRoutes.tenantSettings.getPath(),
                        iconImg: iconNavbarGear,
                    },
                    {
                        label: t('navbar.settings.rolesAndPermissions'),
                        route: AppRoutes.rolesAndPermissions.getPath(),
                        iconImg: iconNavbarGear,
                        hidden: !hasPermission(UserPermission.UserProfitCenterEdit)
                    },
                    {
                        label: t('navbar.settings.applicationSettings'),
                        route: AppRoutes.appSettings.getPath(),
                        iconImg: iconNavbarGear,
                        hidden: !hasPermission(UserPermission.AppSettingsView)
                    },
                ]
            },
            {
                label: t('navbar.notifications'),
                route: AppRoutes.notifications.getPath(),
                iconImg: iconNavbarNotifications,
                badge: unreadNotificationCount > 0 ? <>{unreadNotificationCount}</> : undefined,
                items: [
                    {
                        label: t('navbar.notifications'),
                        route: AppRoutes.notifications.getPath(),
                        showOnStateOnly: NavbarState.collapsed,
                    },
                ]
            },
            {
                label: t('navbar.feedbackAndSupport'),
                route: AppRoutes.help.getPath(),
                iconImg: iconNavbarHelp,
                items: [
                    {
                        label: t('navbar.feedbackAndSupport'),
                        route: AppRoutes.help.getPath(),
                        showOnStateOnly: NavbarState.collapsed,
                    },
                ]
            }
        ]

        return items.map(item => {
            const subItems = item.items?.filter(i => !!i)
            return {
                ...item,
                items: subItems?.length ? subItems : undefined
            }
        })
    }, [globalAssetOverview?.participation, hasPermission, isFinanceBlockHidden, t, unreadNotificationCount])

    function getNavbarItems() {
        return navbarItems ? navbarItems.filter(i => {
            if (!i.showOnStateOnly) {
                return true
            }
            return i.showOnStateOnly === navbarStore.navbarState
        }) : []
    }

    const renderRoute = (route: AppRoute, content: () => React.JSX.Element) => {
        const isAuthRequired = route.authRequired
        const isNavbarEnabled = (!route || route.navbar) && !!commonStore
        const isMainExpanded = isNavbarEnabled && navbarStore.navbarState === NavbarState.collapsed
        const isMainCollapsed = isNavbarEnabled && navbarStore.navbarState === NavbarState.expanded

        return (
            <Route path={route.route} element={<>
                {isAuthRequired && !commonStore ? <Splash /> :
                    <>
                        {isNavbarEnabled && !!commonStore &&
                            <Navbar
                                items={getNavbarItems()}
                                avatarAbbreviation={commonStore.user.nameAbbr}
                                avatarLabel={commonStore.user.fullName} />
                        }
                        <main className={cx([
                            "main",
                            {
                                "main--expanded": isMainExpanded,
                                "main--collapsed": isMainCollapsed,
                            }
                        ])}>
                            <div className="main__content" style={route.minWidth ? { minWidth: route.minWidth } : undefined}>
                                <Suspense fallback={<Splash />}>
                                    {content()}
                                </Suspense>
                            </div>
                        </main>
                    </>
                }
            </>
            } />
        )
    }

    return (
        <>
                <QueryParamProvider adapter={ReactRouter6Adapter}>
                    <Routes>

                        {/* @TODO: think of how we could use a loop here
                            and keep the route content in the route files.
                            currenlty, if we do it, the tests fail */}

                        {/* {Object.keys(Routes).map(routeName => {
                            const route = Routes[routeName]
                            return renderRoute(route)
                        })}
                        {renderRoute()} */}

                        {renderRoute(AppRoutes.dashboard, () => <DashboardPage />)}
                        {renderRoute(AppRoutes.inventory, () => <InventoryOverviewPage />)}
                        {hasPermission(UserPermission.GlobalAssetOverview) && !!globalAssetOverview?.participation && renderRoute(AppRoutes.globalAssetOverview, () => <GlobalAssetOverviewPage />)}
                        {hasPermission(UserPermission.AssetCreate) && renderRoute(AppRoutes.assetImport, () => <AssetImportPage />)}
                        {hasPermission(UserPermission.AssetEdit) && renderRoute(AppRoutes.manageReferenceValues, () => <ManageReferenceValuesPage />)}
                        {hasPermission(UserPermission.AssetCreate) && renderRoute(AppRoutes.assetCreate, () => <AssetPage workflow={AssetWorkflow.create} />)}
                        {hasPermission(UserPermission.AssetEdit) && renderRoute(AppRoutes.assetEdit, () => <AssetPage workflow={AssetWorkflow.edit} />)}
                        {renderRoute(AppRoutes.articleCreate, () => <ArticlePage workflow={ArticleWorkflow.create} />)}
                        {renderRoute(AppRoutes.articleEdit, () => <ArticlePage workflow={ArticleWorkflow.edit} />)}
                        {hasPermission(UserPermission.ReservationEdit) && renderRoute(AppRoutes.reservationCreate, () => <ReservationPage workflow={ReservationWorkflow.create} />)}
                        {hasPermission(UserPermission.ReservationEdit) && renderRoute(AppRoutes.reservationEdit, () => <ReservationPage workflow={ReservationWorkflow.edit} />)}
                        {hasPermission(UserPermission.ReservationEdit) && renderRoute(AppRoutes.reservationCopy, () => <ReservationPage workflow={ReservationWorkflow.copy} />)}
                        {hasPermission(UserPermission.OutgoingInvoicesCreate) && hasPermission(UserPermission.ReservationEdit) && hasPermission(UserPermission.ShippingOrdersCreate) && renderRoute(AppRoutes.earlyReturn, () => <EarlyReturnPage />)}
                        {hasPermission(UserPermission.OfferEdit) && renderRoute(AppRoutes.offerCreate, () => <OfferPage key="create" workflow={OfferWorkflow.create} />)}
                        {hasPermission(UserPermission.OfferEdit) && renderRoute(AppRoutes.offerEdit, () => <OfferPage key="edit" workflow={OfferWorkflow.edit} />)}
                        {renderRoute(AppRoutes.prepareShipping, () => <PrepareShippingPage />)}
                        {hasPermission(UserPermission.CalculationDetailsView) && renderRoute(AppRoutes.calculationDetails, () => <CalculationDetailsPage />)}
                        {renderRoute(AppRoutes.scheduler, () => <SchedulerPage />)}
                        {(hasPermission(UserPermission.OutgoingInvoicesView) || hasPermission(UserPermission.OutgoingInvoicesCreate)) && renderRoute(AppRoutes.outgoingInvoices, () => <OutgoingInvoicesPage />)}
                        {renderRoute(AppRoutes.requests, () => <RequestsPage />)}
                        {renderRoute(AppRoutes.offers, () => <OffersPage onCreateOfferClick={hasPermission(UserPermission.OfferEdit) ? () => setShowCreateOfferModal(true) : undefined} />)}
                        {renderRoute(AppRoutes.shippingOrders, () => <ShippingOrdersPage />)}
                        {hasPermission(UserPermission.IncomingInvoicesView) && renderRoute(AppRoutes.invoices, () => <InvoicesPage />)}
                        {hasPermission(UserPermission.AssignInvoiceAmountView) && renderRoute(AppRoutes.invoiceAssignAmount, () => <InvoiceAssignAmountPage />)}
                        {hasPermission(UserPermission.InvoicesImport) && renderRoute(AppRoutes.invoiceImport, () => <InvoiceImportPage />)}
                        {hasPermission(UserPermission.EvaluationAsset) && renderRoute(AppRoutes.valuations, () => <ValuationsPage />)}
                        {hasPermission(UserPermission.FairValueEvaluationView) && renderRoute(AppRoutes.fairValueEvaluation, () => <FairValueEvaluationPage />)}
                        {hasPermission(UserPermission.OutgoingInvoicesCreate) && renderRoute(AppRoutes.outgoingInvoiceCreate, () => <OutgoingInvoiceCreatePage />)}
                        {hasPermission(UserPermission.AdHocInvoiceCreate) && renderRoute(AppRoutes.adHocOutgoingInvoiceCreate, () => <AdHocOutgoingInvoiceCreatePage />)}
                        {hasPermission(UserPermission.OutgoingInvoicesCreate) && renderRoute(AppRoutes.cancelInvoices, () => <CancelInvoicesPage />)}
                        {(hasPermission(UserPermission.OutgoingInvoicesView) || hasPermission(UserPermission.OutgoingInvoicesCreate)) && renderRoute(AppRoutes.outgoingInvoiceView, () => <OutgoingInvoicePage />)}
                        {(hasPermission(UserPermission.OutgoingInvoicesView) || hasPermission(UserPermission.OutgoingInvoicesCreate)) && renderRoute(AppRoutes.outgoingInvoiceDraftByDate, () => <OutgoingInvoiceDraftByDatePage />)}
                        {hasPermission(UserPermission.EvaluationAsset) && renderRoute(AppRoutes.valuation, () => <ValuationPage />)}
                        {hasPermission(UserPermission.MonthlyClosing) && renderRoute(AppRoutes.monthlyClosing, () => <MonthlyClosingPage />)}
                        {hasPermission(UserPermission.LabelsView) && renderRoute(AppRoutes.linkLabels, () => <LinkLabelsPage />)}
                        {hasPermission(UserPermission.LabelsLink) && renderRoute(AppRoutes.linkLabelToAsset, () => <LinkLabelToAssetPage />)}
                        {renderRoute(AppRoutes.tenantSettings, () => <TenantSettingsPage />)}
                        {hasPermission(UserPermission.AppSettingsView) && renderRoute(AppRoutes.appSettings, () => <AppSettingsPage />)}
                        {renderRoute(AppRoutes.productCategories, () => <ProductCategoriesPage />)}
                        {renderRoute(AppRoutes.liebherrMachineArticlesRoute, () => <ArticlesPage title={t('articles.liebherrMachineArticles')} isExtManufacturer={false} />)}
                        {renderRoute(AppRoutes.userSettings, () => <UserSettingsPage />)}
                        {renderRoute(AppRoutes.notifications, () => <NotificationsPage />)}
                        {renderRoute(AppRoutes.help, () => <HelpPage />)}
                        {renderRoute(AppRoutes.noCompany, () => <NoCompanyPage />)}
                        {renderRoute(AppRoutes.noAccess, () => <NoAccessPage />)}
                        {renderRoute(AppRoutes.stocktakings, () => <StocktakingsPage />)}
                        {hasPermission(UserPermission.AssetMaintenanceView) && renderRoute(AppRoutes.maintenance, () => <MaintenancePage />)}
                        {hasPermission(UserPermission.RentToBuyEvents) && renderRoute(AppRoutes.rentToBuyChecklist, () => <RentToBuyChecklistPage />)}
                        {hasPermission(UserPermission.UserProfitCenterEdit) && renderRoute(AppRoutes.rolesAndPermissions, () => <RolesAndPermissionsPage />)}
                        {renderRoute(AppRoutes.stocktakingNotScannedAssets, () => <StocktakingNotScannedAssetsPage />)}
                        {hasPermission(UserPermission.CustomersDashboardView) && renderRoute(AppRoutes.customerOverview, () => <CustomerOverviewPage />)}
                        {hasPermission(UserPermission.CustomersDashboardView) && renderRoute(AppRoutes.customer, () => <CustomerPage />)}

                        {renderRoute(AppRoutes.notFound, () => <NotFoundPage />)}
                    </Routes>

                    {/*
                        wait for common store to be initialized,
                        because it prefetches profit centers
                    */}
                    {!!commonStore &&
                        <UnreadNotificationsRefresh />
                    }

                    <PictureModals />
                    <AlertModals />
                    <ConfirmModals />
                    <ArticleSidebars />
                    <AssetSidebars />
                    <GlobalAssetOverviewAssetSidebars />
                    <ReservationSidebars />
                    <InvoiceSidebars />
                    <GlobalReservationSidebarHandler />
                    <GlobalAssetSidebarHandler />

                    {showReferenceNumberSelectModal &&
                        <ReferenceNumberSelectModal
                            onClose={() => setShowReferenceNumberSelectModal(false)}
                        />
                    }
                    {showCreateOfferModal &&
                        <OfferCreateModal
                            onClose={() => setShowCreateOfferModal(false)}
                        />
                    }
                </QueryParamProvider>
        </>
    )
}

// Single splat route: the data router owns navigation (history, blocking via
// useBlocker), while AppShell keeps the descendant <Routes> tree. Full route
// objects can come with the react-router 7 migration.
const router = createBrowserRouter([{ path: "*", element: <AppShell /> }])

registerNavigate((to, options) => {
    router.navigate(to, options)
})

const App = () => (
    <>
        <RouterProvider router={router} />
        <ToastMessages />
    </>
)

export default App
